import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiBell, FiCalendar, FiChevronDown, FiSearch, FiShoppingCart, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";

interface CartProduct {
  name: string;
  image: string;
  measurement: string;
  type: string;
  quantity: number;
  totalPrice: number;
  pricePerQuantity: number;
  productId: string;
  _id: string;
}

interface CartItem {
  id: string; // MongoDB _id
  cartId: string;
  customerName: string;
  status: string; // e.g. "active", "processing", "draft", "abandoned"
  items: CartProduct[];
  itemCount: number;
  value: number;
  lastActive: string;
  agent: string;
  agentId: string;
  updatedAt: string;
  createdAt: string;
  activityDate: string;
}

interface OverviewCounts {
  allCarts: number;
  activeCart: number;
  processing: number;
  draftIncomplete: number;
  abandoned: number;
  label: string;
}

interface SummaryCounts {
  all: number;
  active: number;
  processing: number;
  draft: number;
  abandoned: number;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Live Cart by ID details interfaces
interface CartDetailsHeader {
  cartId: string;
  customerName: string;
  status: string;
}

interface CartDetailsCustomer {
  name: string;
  email: string | null;
  phoneNumber: string | null;
}

interface CartDetailsSummary {
  items: number;
  amount: number;
  paymentMethod: string;
  lastActive: string;
}

interface CartDetailsAgent {
  id: string;
  name: string;
  role: string;
}

interface CartDetailsProduct {
  name: string;
  image: string | null;
  price: number;
  quantity: number;
  subTotal: number;
}

interface CartDetailsTimeline {
  event: string;
  title: string;
  occurredAt: string;
  meta: string;
}

interface CartDetailsData {
  header: CartDetailsHeader;
  customer: CartDetailsCustomer;
  cartSummary: CartDetailsSummary;
  agentOnCart: CartDetailsAgent;
  products: CartDetailsProduct[];
  cartTimeline: CartDetailsTimeline[];
}

const FILTER_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "month", label: "This Month" },
  { value: "yearly", label: "This Year" },
] as const;

const STATUS_TABS = [
  { value: "all", label: "All Carts", summaryKey: "all" },
  { value: "active", label: "Active", summaryKey: "active" },
  { value: "processing", label: "Processing", summaryKey: "processing" },
  { value: "draft", label: "Draft", summaryKey: "draft" },
  { value: "abandoned", label: "Abandoned", summaryKey: "abandoned" },
] as const;

type FilterType = typeof FILTER_OPTIONS[number]["value"];
type StatusType = typeof STATUS_TABS[number]["value"];

export default function LiveCart() {
  const [filter, setFilter] = useState<FilterType>("today");
  const [status, setStatus] = useState<StatusType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [carts, setCarts] = useState<CartItem[]>([]);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(carts.length / itemsPerPage) || 1;
  const currentCarts = carts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const [overviewCounts, setOverviewCounts] = useState<OverviewCounts | null>(null);
  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // States for Cart Details fetching by ID
  const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
  const [selectedCartDetails, setSelectedCartDetails] = useState<CartDetailsData | null>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount);

  const formatStatus = (s: string) => {
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const fetchLiveCarts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      queryParams.append("filter", filter);
      queryParams.append("status", status);
      queryParams.append("limit", "100000");
      if (debouncedSearchQuery.trim()) {
        queryParams.append("q", debouncedSearchQuery.trim());
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/live-carts-summary?${queryParams.toString()}`,
        { headers: authHeaders(token) }
      );

      if (res.data?.success) {
        setCarts(res.data.data.data ?? []);
        setOverviewCounts(res.data.data.overviewCounts ?? null);
        setSummaryCounts(res.data.data.summary ?? null);
      } else {
        setError("Unexpected response from server");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch live carts";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [filter, status, debouncedSearchQuery]);

  const fetchCartDetails = useCallback(async (cartId: string) => {
    setIsDetailsLoading(true);
    setDetailsError(null);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/live-carts/${cartId}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setSelectedCartDetails(res.data.data);
      } else {
        setDetailsError("Unexpected response format from server");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch cart details";
      setDetailsError(msg);
      toast.error(msg);
    } finally {
      setIsDetailsLoading(false);
    }
  }, []);

  // Debounce search query input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset page on new search query
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchLiveCarts();
  }, [fetchLiveCarts]);

  // Fetch details when a cart ID is selected
  useEffect(() => {
    if (selectedCartId) {
      fetchCartDetails(selectedCartId);
    } else {
      setSelectedCartDetails(null);
      setDetailsError(null);
    }
  }, [selectedCartId, fetchCartDetails]);

  // Reset page when filter or status tabs change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, status]);

  const handleExportCSV = () => {
    if (!carts || carts.length === 0) {
      toast.error("No cart data available to export");
      return;
    }

    const headers = ["Cart ID", "Customer Name", "Status", "Items Count", "Value (NGN)", "Last Active", "Agent"];
    const rows = carts.map((cart) => [
      cart.cartId,
      cart.customerName,
      cart.status,
      cart.itemCount,
      cart.value,
      cart.lastActive,
      cart.agent,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `live_carts_report_${filter}_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Live carts report exported successfully");
  };

  const getFilterLabel = () => {
    return FILTER_OPTIONS.find((f) => f.value === filter)?.label || "Today";
  };

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans relative">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950 tracking-tight">LIVE CART</h1>
          <p className="text-gray-400 text-sm mt-0.5">Live fulfilment queue — all statuses</p>
        </div>

        <div className="flex items-center gap-3 self-stretch md:self-auto flex-wrap">
          <WalkInAlertsPill />

          {/* Date range filter dropdown */}
          <div className="relative">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterType)}
              className="appearance-none bg-white border border-gray-100 rounded-lg pl-10 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:border-[#008060] cursor-pointer shadow-sm"
            >
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <FiCalendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#008060] pointer-events-none" />
            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>

          <button
            onClick={handleExportCSV}
            className="flex items-center justify-between gap-3 px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm min-w-[150px]"
          >
            <span>Export Summary</span>
            <FiChevronDown className="text-white" />
          </button>
        </div>
      </div>

      {/* Row of Metrics (4 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Metric 1: Active Carts */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Active Cart</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            {isLoading ? (
              <div className="h-7 w-12 bg-gray-100 rounded animate-pulse my-1" />
            ) : (
              <h3 className="text-[26px] font-black text-gray-900 leading-none">
                {overviewCounts?.activeCart ?? 0}
              </h3>
            )}
            <span className="text-[#008060] text-sm font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>

        {/* Metric 2: Processing Carts */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Processing</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            {isLoading ? (
              <div className="h-7 w-12 bg-gray-100 rounded animate-pulse my-1" />
            ) : (
              <h3 className="text-[26px] font-black text-gray-900 leading-none">
                {overviewCounts?.processing ?? 0}
              </h3>
            )}
            <span className="text-[#008060] text-sm font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>

        {/* Metric 3: Draft (Incomplete) */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Draft (Incomplete)</p>
          {isLoading ? (
            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse my-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {overviewCounts?.draftIncomplete ?? 0}
            </h3>
          )}
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>

        {/* Metric 4: Abandoned */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Abandoned</p>
          {isLoading ? (
            <div className="h-7 w-12 bg-gray-100 rounded animate-pulse my-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {overviewCounts?.abandoned ?? 0}
            </h3>
          )}
          <p className="text-gray-400 text-[11px] font-medium mt-1">{getFilterLabel()}</p>
        </div>
      </div>

      {/* Tabs & Search controls */}
      <div className="flex flex-col gap-4 mt-2">
        <div className="flex bg-[#EAF5EF]/40 border border-gray-100/50 p-1 rounded-xl w-fit gap-1 select-none flex-wrap">
          {STATUS_TABS.map((tab) => {
            const summaryKey = tab.summaryKey;
            const count = summaryCounts ? summaryCounts[summaryKey] : 0;
            const isTabActive = status === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setStatus(tab.value)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  isTabActive
                    ? "bg-[#EAF5EF] text-[#008060] shadow-sm border border-emerald-100"
                    : "text-gray-400 hover:text-gray-700 font-semibold"
                }`}
              >
                {tab.label} ({count})
              </button>
            );
          })}
        </div>

        <div className="relative w-full">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by Customer name or Agent..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-[#008060] transition-all shadow-sm placeholder-gray-400"
          />
        </div>
      </div>

      {/* Live Cart table */}
      {error ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
          <p className="text-sm text-gray-500 font-medium mb-3">{error}</p>
          <button
            onClick={() => {
              fetchLiveCarts();
            }}
            className="px-4 py-2 bg-[#008060] text-white rounded-lg text-xs font-bold hover:bg-[#006b50] transition-colors shadow-sm"
          >
            Retry Request
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5 mt-2">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-[#EAF5EF] text-[#008060] text-xs font-bold rounded-lg">
                  <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Cart/ Customer</th>
                  <th className="p-3.5 font-bold tracking-wide">Status</th>
                  <th className="p-3.5 font-bold tracking-wide">Items</th>
                  <th className="p-3.5 font-bold tracking-wide">Value</th>
                  <th className="p-3.5 font-bold tracking-wide">Last Active</th>
                  <th className="p-3.5 font-bold tracking-wide">Agent</th>
                  <th className="p-3.5 pr-4 rounded-r-lg font-bold tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                      <td className="p-3.5 pl-4">
                        <div className="h-4 w-28 bg-gray-100 rounded mb-1" />
                        <div className="h-3.5 w-36 bg-gray-50 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-20 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-12 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-24 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-20 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5">
                        <div className="h-4 w-28 bg-gray-100 rounded" />
                      </td>
                      <td className="p-3.5 pr-4">
                        <div className="h-4 w-20 bg-gray-100 rounded" />
                      </td>
                    </tr>
                  ))
                ) : currentCarts.length > 0 ? (
                  currentCarts.map((cart) => {
                    const statusStr = cart.status?.toLowerCase() || "";
                    const isStatusActive = statusStr === "active" || statusStr === "processing";
                    const isStatusDraft = statusStr === "draft";
                    return (
                      <tr key={cart.id} className="hover:bg-gray-50/30 transition-colors">
                        <td className="p-3.5 pl-4">
                          <p className="text-gray-900 font-bold">{cart.cartId}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{cart.customerName}</p>
                        </td>
                        <td className="p-3.5">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                isStatusActive
                                  ? "bg-emerald-500"
                                  : isStatusDraft
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                              }`}
                            />
                            <span
                              className={`text-xs font-bold ${
                                isStatusActive
                                  ? "text-emerald-500"
                                  : isStatusDraft
                                    ? "text-amber-500"
                                    : "text-red-500"
                              }`}
                            >
                              {formatStatus(cart.status)}
                            </span>
                          </div>
                        </td>
                        <td className="p-3.5 text-gray-900">{cart.itemCount}</td>
                        <td className="p-3.5 text-gray-900 font-bold">{formatCurrency(cart.value)}</td>
                        <td className="p-3.5 text-gray-500">{cart.lastActive}</td>
                        <td className="p-3.5">
                          <span className="text-[#008060] font-bold">{cart.agent}</span>
                        </td>
                        <td className="p-3.5 pr-4">
                          <button
                            onClick={() => setSelectedCartId(cart.id)}
                            className="text-blue-600 hover:text-blue-800 transition-colors text-xs font-bold"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-400">
                      No active baskets match the filter criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {!isLoading && carts.length > 0 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cart Details Modal */}
      {selectedCartId && (
        <div className="fixed inset-0 bg-black/45 flex items-center justify-center p-4 z-50 animate-fadeIn overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto border border-gray-100/50 p-6 flex flex-col gap-6 relative scrollbar-thin">
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-gray-50 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gray-50 rounded-xl text-gray-600">
                  <FiShoppingCart className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {selectedCartDetails?.header?.cartId || "Loading..."}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {selectedCartDetails?.header?.customerName || "..."}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedCartDetails?.header?.status && (
                  <span className="px-3.5 py-1 bg-[#EAF9F2] text-[#008060] text-xs font-bold rounded-full">
                    {formatStatus(selectedCartDetails.header.status)}
                  </span>
                )}
                <button
                  onClick={() => {
                    setSelectedCartId(null);
                    setSelectedCartDetails(null);
                    setDetailsError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50 transition-colors ml-2"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {isDetailsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-10 h-10 border-4 border-[#EAF5EF] border-t-[#008060] rounded-full animate-spin" />
                <p className="text-xs text-gray-400 font-semibold tracking-wide animate-pulse">
                  Retrieving live cart details...
                </p>
              </div>
            ) : detailsError ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
                <p className="text-sm text-gray-500 font-medium mb-3">{detailsError}</p>
                <button
                  onClick={() => fetchCartDetails(selectedCartId)}
                  className="px-4 py-2 bg-[#008060] text-white rounded-lg text-xs font-bold hover:bg-[#006b50] transition-colors shadow-sm"
                >
                  Retry Load
                </button>
              </div>
            ) : selectedCartDetails ? (
              <>
                {/* Customer Details */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Customer</h4>
                  <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex flex-col gap-2.5">
                    <p className="text-sm text-gray-500 font-medium">
                      Name:{" "}
                      <span className="text-gray-800 font-semibold ml-1">
                        {selectedCartDetails.customer.name || "Walk-in Customer"}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Cart Summary Grid */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cart Summary</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50/30 border border-gray-100 p-3 rounded-2xl text-center flex flex-col justify-center items-center min-h-[75px]">
                      <span className="text-lg font-black text-gray-900">
                        {selectedCartDetails.cartSummary.items}
                      </span>
                      <span className="text-[10px] font-bold text-gray-400 tracking-wide uppercase mt-1">
                        Items
                      </span>
                    </div>
                    <div className="bg-gray-50/30 border border-gray-100 p-3 rounded-2xl text-center flex flex-col justify-center items-center min-h-[75px]">
                      <span className="text-lg font-black text-[#008060]">
                        {formatCurrency(selectedCartDetails.cartSummary.amount)}
                      </span>
                      <span className="text-[10px] font-bold text-gray-400 tracking-wide uppercase mt-1">
                        Amount
                      </span>
                    </div>
                    <div className="bg-gray-50/30 border border-gray-100 p-3 rounded-2xl text-center flex flex-col justify-center items-center min-h-[75px]">
                      <span className="text-sm font-bold text-gray-900">
                        {formatStatus(selectedCartDetails.cartSummary.paymentMethod)}
                      </span>
                      <span className="text-[10px] font-bold text-[#008060] tracking-wide uppercase mt-1">
                        Payment
                      </span>
                    </div>
                  </div>
                </div>

                {/* Agent on Cart */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Agent on Cart</h4>
                  <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#EAF9F2] text-[#008060] font-black text-sm flex items-center justify-center select-none shadow-sm">
                      {selectedCartDetails.agentOnCart.name
                        ? selectedCartDetails.agentOnCart.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)
                        : "UN"}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{selectedCartDetails.agentOnCart.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{selectedCartDetails.agentOnCart.role}</p>
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Products</h4>
                  <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-gray-400 text-[10px] font-bold">
                          <th className="py-2.5 px-4 font-bold tracking-wide">PRODUCTS</th>
                          <th className="py-2.5 px-3 font-bold tracking-wide">PRICE</th>
                          <th className="py-2.5 px-3 text-center font-bold tracking-wide">QUANTITY</th>
                          <th className="py-2.5 px-4 text-right font-bold tracking-wide">SUB-TOTAL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 text-gray-700 font-medium">
                        {selectedCartDetails.products && selectedCartDetails.products.length > 0 ? (
                          selectedCartDetails.products.map((prod, pIdx) => (
                            <tr key={pIdx} className="hover:bg-gray-50/20 transition-colors">
                              <td className="py-3 px-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100/50 flex items-center justify-center overflow-hidden shrink-0">
                                  {prod.image ? (
                                    <img
                                      src={prod.image}
                                      alt={prod.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = "";
                                        (e.target as HTMLImageElement).style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <FiShoppingCart className="w-5 h-5 text-orange-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-bold text-gray-900">{prod.name}</p>
                                </div>
                              </td>
                              <td className="py-3 px-3 text-gray-500">{formatCurrency(prod.price)}</td>
                              <td className="py-3 px-3 text-center text-gray-900 font-bold">{prod.quantity}</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-bold">{formatCurrency(prod.subTotal)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center py-4 text-gray-400">
                              No items in this basket.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cart Timeline</h4>
                  <div className="border border-gray-100 rounded-2xl p-5 bg-gray-50/15 flex flex-col gap-5 relative">
                    {selectedCartDetails.cartTimeline && selectedCartDetails.cartTimeline.length > 0 ? (
                      selectedCartDetails.cartTimeline.map((step, sIdx, arr) => {
                        const isLast = sIdx === arr.length - 1;
                        return (
                          <div key={sIdx} className="flex gap-4 relative">
                            {!isLast && (
                              <span className="absolute left-2.5 top-6 bottom-0 w-0.5 bg-emerald-500" />
                            )}
                            <span className="w-5 h-5 rounded-full flex items-center justify-center z-10 border-4 bg-white border-emerald-500">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            </span>
                            <div className="flex flex-col gap-0.5">
                              <p className="text-xs font-bold text-gray-900">{step.title}</p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(step.occurredAt).toLocaleTimeString("en-US", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                                &nbsp;&middot;&nbsp; {step.meta}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-2">No timeline recorded.</p>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
